import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';

/**
 * Netflix-style horizontal carousel of example videos.
 * - Compact 208px-wide 16:9 tiles.
 * - Full-height edge-gradient arrows with circular chevrons; auto-hide at scroll ends.
 * - Mobile: arrows hidden; native touch-swipe works.
 * - **Static JPEG thumbnails by default** — videos are NOT fetched until the user clicks.
 *   This makes the page load near-instant even with 9 examples on screen.
 * - Click → opens the existing preview modal (via parent's `onSelect`).
 * - No filename labels (admin-only metadata).
 */
export default function ExampleVideosRow({ videos, onSelect }) {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanLeft(scrollLeft > 4);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows, videos.length]);

  const scrollByPage = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.max(el.clientWidth * 0.85, 220);
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  if (!videos || videos.length === 0) return null;

  return (
    <div className="relative max-w-6xl mx-auto mb-8 sm:mb-10 group/row" data-testid="example-videos-section">
      <button
        type="button"
        onClick={() => scrollByPage(-1)}
        aria-label="Scroll examples left"
        data-testid="examples-scroll-left"
        className={`hidden sm:flex absolute left-0 top-0 bottom-0 z-20 items-center justify-start pl-2 w-20 transition-opacity duration-200 ${
          canLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          background:
            'linear-gradient(to right, rgba(15,20,25,0.95) 0%, rgba(15,20,25,0.7) 55%, rgba(15,20,25,0) 100%)',
        }}
      >
        <div className="w-10 h-10 rounded-full bg-[#1A2035]/90 border border-[#FFB238]/30 flex items-center justify-center hover:bg-[#FFB238] hover:border-[#FFB238] transition-colors">
          <ChevronLeft className="w-5 h-5 text-white" />
        </div>
      </button>

      <button
        type="button"
        onClick={() => scrollByPage(1)}
        aria-label="Scroll examples right"
        data-testid="examples-scroll-right"
        className={`hidden sm:flex absolute right-0 top-0 bottom-0 z-20 items-center justify-end pr-2 w-20 transition-opacity duration-200 ${
          canRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          background:
            'linear-gradient(to left, rgba(15,20,25,0.95) 0%, rgba(15,20,25,0.7) 55%, rgba(15,20,25,0) 100%)',
        }}
      >
        <div className="w-10 h-10 rounded-full bg-[#1A2035]/90 border border-[#FFB238]/30 flex items-center justify-center hover:bg-[#FFB238] hover:border-[#FFB238] transition-colors">
          <ChevronRight className="w-5 h-5 text-white" />
        </div>
      </button>

      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto pb-2 px-4 sm:px-6 snap-x snap-mandatory scroll-smooth no-scrollbar"
        data-testid="examples-scroller"
      >
        {videos.map((vid, idx) => (
          <ExampleTile
            key={`${vid.url}-${idx}`}
            video={vid}
            index={idx}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

function ExampleTile({ video, index, onSelect }) {
  // Static-thumbnail-only mode: we NEVER mount a <video> element here. The actual
  // video bytes are only fetched when the user clicks the tile (parent opens a
  // preview modal that handles playback).
  const [thumbError, setThumbError] = useState(false);
  const hasThumb = !!video.thumbnail_url && !thumbError;

  return (
    <button
      type="button"
      className="relative flex-shrink-0 w-[276px] sm:w-[300px] aspect-video bg-[#0F1419] rounded-lg overflow-hidden border border-[#222D4A] hover:border-[#FFB238] transition-all cursor-pointer group snap-start"
      onClick={() => onSelect?.(video)}
      data-testid={`example-video-${index}`}
      aria-label="Play example"
    >
      {hasThumb ? (
        <img
          src={video.thumbnail_url}
          alt=""
          loading="lazy"
          decoding="async"
          fetchpriority="low"
          width={300}
          height={169}
          className="w-full h-full object-cover"
          onError={() => setThumbError(true)}
        />
      ) : (
        // Fallback gradient placeholder when no thumbnail has been generated yet.
        // Admin can populate thumbnails via `POST /api/admin/examples/generate-thumbnails`.
        <div className="w-full h-full bg-gradient-to-br from-[#222D4A] via-[#1A2035] to-[#0F1419]" />
      )}
      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div className="w-10 h-10 rounded-full bg-[#FFB238] flex items-center justify-center">
          <Play className="w-5 h-5 text-[#0F1419] ml-0.5" fill="#0F1419" />
        </div>
      </div>
    </button>
  );
}
